import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { rmsApi } from '../../../state/axios';
import WidgetLoading from '../../../components/widgetLoading';
import BusinessProfilePanel from '../../askVantageV2/components/BusinessProfilePanel';
import {
  AV_PROFILE_OPEN_EVENT,
  type ProfileOpenDetail
} from '../../askVantageV2/components/AskVantageResponse';
import { SignalCard } from '../components/SignalCard';
import { SignalDetailsDrawer } from '../components/SignalDetailsDrawer';
import { HealthSummaryBar, type SignalFilterKey } from '../components/HealthSummaryBar';
import { CategoryFilter, CategoryKey } from '../components/CategoryFilter';
import { SignalsComingSoon } from '../components/SignalsComingSoon';
import { SIGNALS_FONT_FAMILY } from '../theme/constants';
import { useSignals } from '../hooks/useSignals';
import type { Signal, SignalsSummary } from '../types';

// Default empty summary — pre-shaped so the bar can render skeleton-free
// while data loads. The "evaluating" cards underneath cover that phase
// without needing a top-level loading spinner.
const EMPTY_SUMMARY: SignalsSummary = {
  red: 0,
  amber: 0,
  green: 0,
  unconfigured: 0,
  evaluating: 0
};

const summaryFor = (signals: Signal[]): SignalsSummary => {
  return signals.reduce<SignalsSummary>((acc, signal) => {
    if (!signal.is_hidden && signal.status in acc) {
      acc[signal.status as keyof SignalsSummary] += 1;
    }
    return acc;
  }, { ...EMPTY_SUMMARY });
};

export default function SignalsDashboard() {
  const companyDetails = useSelector(
    (state: RootState) => state.company?.company_details as any
  );
  const signalsStatus = companyDetails?.signals_status as
    | 'enabled'
    | 'coming_soon'
    | 'disabled'
    | undefined;
  const isMaintenance = companyDetails?.maintenance_mode?.enabled;

  // Phase 1 contract from `/api/v1/me`: anything other than `"enabled"`
  // shows the placeholder. Default to placeholder when the value isn't
  // present yet (e.g. mid /me hydration) — safer than flashing the
  // dashboard for half a second before swapping.
  if (signalsStatus !== 'enabled') {
    return <SignalsComingSoon />;
  }

  return <SignalsActiveDashboard isMaintenance={isMaintenance} />;
}

interface ActiveDashboardProps {
  isMaintenance: boolean;
}

const SignalsActiveDashboard = ({ isMaintenance }: ActiveDashboardProps) => {
  const { data, error, isLoading, mutate } = useSignals();
  const [statusFilter, setStatusFilter] = useState<SignalFilterKey>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryKey>('all');
  const [savingSignalId, setSavingSignalId] = useState<string | null>(null);
  const [expandedSignal, setExpandedSignal] = useState<Signal | null>(null);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [profileInitialCategory, setProfileInitialCategory] = useState<string | undefined>();
  const [profileFocus, setProfileFocus] = useState<ProfileOpenDetail | undefined>();

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ProfileOpenDetail>).detail;
      setProfileInitialCategory(detail?.category);
      setProfileFocus(detail);
      setShowProfilePanel(true);
    };

    window.addEventListener(AV_PROFILE_OPEN_EVENT, handler);
    return () => window.removeEventListener(AV_PROFILE_OPEN_EVENT, handler);
  }, []);

  const signals: Signal[] = data?.signals ?? [];
  const activeSignals = useMemo(() => signals.filter((s) => !s.is_hidden), [signals]);
  const hiddenSignals = useMemo(() => signals.filter((s) => s.is_hidden), [signals]);
  const summary = data?.summary ?? EMPTY_SUMMARY;

  const viewingHidden = statusFilter === 'hidden';

  const categories = useMemo<CategoryKey[]>(() => {
    const pool = viewingHidden ? hiddenSignals : activeSignals;
    const seen = new Set<CategoryKey>();
    pool.forEach((s) => seen.add(s.category));
    return Array.from(seen);
  }, [activeSignals, hiddenSignals, viewingHidden]);

  const visibleSignals = useMemo(() => {
    const pool = viewingHidden ? hiddenSignals : activeSignals;
    return pool.filter((s) => {
      if (!viewingHidden && statusFilter !== 'all' && s.status !== statusFilter) {
        return false;
      }
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
      return true;
    });
  }, [activeSignals, hiddenSignals, viewingHidden, statusFilter, categoryFilter]);

  const total = activeSignals.length;
  const isFirstLoad = isLoading && !data;

  const handleHiddenChange = useCallback(
    async (signal: Signal, hidden: boolean) => {
      setSavingSignalId(signal.signal_id);
      try {
        const response = await rmsApi.patch(`/api/v1/signals/${signal.signal_id}/preferences`, {
          enabled: !hidden
        });
        const updated = response.data.signal as Signal;
        await mutate((current) => {
          if (!current) return current;

          const nextSignals = current.signals.map((item) =>
            item.signal_id === updated.signal_id ? updated : item
          );

          return {
            ...current,
            signals: nextSignals,
            summary: summaryFor(nextSignals)
          };
        }, false);
      } finally {
        setSavingSignalId(null);
      }
    },
    [mutate]
  );

  const handleTunableChange = useCallback(
    async (signal: Signal, tunableValues: Record<string, number | boolean>) => {
      setSavingSignalId(signal.signal_id);
      try {
        const response = await rmsApi.patch(`/api/v1/signals/${signal.signal_id}/preferences`, {
          tunable_overrides: tunableValues
        });
        const updated = response.data.signal as Signal;
        await mutate((current) => {
          if (!current) return current;

          const nextSignals = current.signals.map((item) =>
            item.signal_id === updated.signal_id ? updated : item
          );

          return {
            ...current,
            signals: nextSignals,
            summary: summaryFor(nextSignals)
          };
        }, false);
      } finally {
        setSavingSignalId(null);
      }
    },
    [mutate]
  );

  const handleSettingsOpen = useCallback(async (signal: Signal) => {
    const response = await rmsApi.get(`/api/v1/signals/${signal.signal_id}/preferences`);
    return response.data.tunables || [];
  }, []);

  return (
    <Page topOffset={isMaintenance ? 120 : 60}>
      <BusinessProfilePanel
        show={showProfilePanel}
        onClose={() => setShowProfilePanel(false)}
        initialCategory={profileInitialCategory}
        initialFocus={profileFocus}
      />
      <Inner>
        <Header>
          <HeaderText>
            <PageEyebrow>Sapaad Signals</PageEyebrow>
            <PageTitle>Operational Signals</PageTitle>
            <PageSubtitle>
              The things that need your attention today — grouped by what's worth acting on now.
            </PageSubtitle>
          </HeaderText>
        </Header>

        <Controls>
          <HealthSummaryBar
            summary={summary}
            total={total}
            hiddenCount={hiddenSignals.length}
            activeFilter={statusFilter}
            onFilterChange={setStatusFilter}
          />
          {categories.length > 1 && (
            <CategoryFilter
              categories={categories}
              active={categoryFilter}
              onChange={setCategoryFilter}
            />
          )}
        </Controls>

        {error ? (
          <ErrorState>
            We couldn't load your signals just now. We'll keep trying — please come back in a moment.
          </ErrorState>
        ) : isFirstLoad ? (
          <Grid>
            <SkeletonCard>
              <WidgetLoading />
            </SkeletonCard>
            <SkeletonCard>
              <WidgetLoading />
            </SkeletonCard>
            <SkeletonCard>
              <WidgetLoading />
            </SkeletonCard>
          </Grid>
        ) : visibleSignals.length === 0 ? (
          <EmptyState>
            <EmptyTitle>Nothing here right now</EmptyTitle>
            <EmptyDetail>
              {viewingHidden
                ? hiddenSignals.length === 0
                  ? "You haven't hidden any signals yet."
                  : 'No hidden signals match the current filters.'
                : total === 0
                  ? "Signals haven't been evaluated for your account yet. Check back in a minute."
                  : 'No signals match the current filters. Try selecting "All".'}
            </EmptyDetail>
          </EmptyState>
        ) : (
          <Grid>
            {visibleSignals.map((signal, idx) => (
              <CardSlot key={signal.signal_id} $delay={idx * 60}>
                <SignalCard
                  signal={signal}
                  onHiddenChange={handleHiddenChange}
                  onTunableChange={handleTunableChange}
                  onSettingsOpen={handleSettingsOpen}
                  onExpand={setExpandedSignal}
                  isPreferenceSaving={savingSignalId === signal.signal_id}
                />
              </CardSlot>
            ))}
          </Grid>
        )}
        <SignalDetailsDrawer signal={expandedSignal} onClose={() => setExpandedSignal(null)} />
      </Inner>
    </Page>
  );
};

// ── Animations ────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const skeletonShimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ── Styled components ─────────────────────────────────────────────────────

const Page = styled.div<{ topOffset: number }>`
  padding-top: ${({ topOffset }) => topOffset}px;
  min-height: 100vh;
  background: ${({ theme }) => theme.signals.page_bg};
  font-family: ${SIGNALS_FONT_FAMILY};
  animation: ${fadeIn} 0.3s ease;
`;

const Inner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 36px 50px 80px;

  @media (max-width: 768px) {
    padding: 24px 18px 60px;
  }
`;

const Header = styled.header`
  margin-bottom: 24px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PageEyebrow = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.text_muted};
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.signals.text_primary};
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.signals.text_secondary};
  max-width: 620px;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 22px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-auto-rows: 1fr;
  align-items: stretch;
  gap: 20px;
`;

// `display: contents` keeps each slot semantically transparent while still
// allowing us to inject a per-card animation-delay for the staggered
// entry effect from the PRD.
const CardSlot = styled.div<{ $delay: number }>`
  display: contents;

  & > article {
    animation-delay: ${({ $delay }) => $delay}ms;
  }
`;

const SkeletonCard = styled.div`
  position: relative;
  height: 320px;
  border-radius: 20px;
  background: ${({ theme }) => theme.signals.card_bg};
  border: none;
  filter: drop-shadow(rgba(0, 0, 0, 0.05) 5px 5px 10px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) => theme.signals.divider} 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    opacity: 0.25;
    animation: ${skeletonShimmer} 1.6s ease-in-out infinite;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const ErrorState = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: ${({ theme }) => theme.signals.text_secondary};
  background: ${({ theme }) => theme.signals.card_bg};
  border: none;
  border-radius: 20px;
  filter: drop-shadow(rgba(0, 0, 0, 0.05) 5px 5px 10px);
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 80px 24px;
  text-align: center;
  background: ${({ theme }) => theme.signals.card_bg};
  border: none;
  border-radius: 20px;
  filter: drop-shadow(rgba(0, 0, 0, 0.05) 5px 5px 10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.signals.text_primary};
`;

const EmptyDetail = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.signals.text_secondary};
  max-width: 360px;
`;
